// Single source of truth for lane data in the Lanes module. Today it
// fetches /put-away/locations, picks rows where type === "lane", and
// layers a deterministic FE mock for status/totes/auctions/end-date.
// When the BE feed lands, replace the `mockLane(...)` line with the
// real fields off the API row — no other call sites change.
//
// The hook's return shape (`{ lanes, refresh, refreshing, lastUpdatedAt }`)
// is the contract the rest of the Lanes UI depends on, including the
// future SSE/polling path.

import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetch } from "../../api/apiFetch";
import { mockLane } from "./mockLaneData";
import type { StatusSlug } from "./laneStatus";

export interface Lane {
    barcode: string;
    warehouse: string;
    zone: string | null;
    walk_order: number | null;
    status: StatusSlug;
    totes: number;
    auctions: number;
    auction_end_date: string | null;
}

interface LocationRow {
    barcode: string;
    warehouse: string;
    type: string | null;
    zone: string | null;
    walk_order: number | null;
}

export interface UseLanesResult {
    lanes: Lane[];
    /** Full set across every warehouse — used for KPI / pill totals. */
    allLanes: Lane[];
    /** Distinct warehouse list discovered from the locations table. */
    warehouses: string[];
    refresh: () => Promise<void>;
    refreshing: boolean;
    /** Whether the very first fetch has resolved. */
    initialLoaded: boolean;
    lastUpdatedAt: number | null;
    error: boolean;
    /**
     * Manually set a lane's status. No-ops (lane is locked) when the lane
     * carries any totes or auctions — only an empty lane can be
     * reassigned. See lanesCanChangeStatus.
     */
    setLaneStatus: (barcode: string, status: StatusSlug) => void;
}

export const STALE_THRESHOLD_MS = 60_000;

// A lane's status may only be changed by hand once it is empty: any totes
// or auctions sitting on it lock the status. Single source of truth for
// the rule so the UI (drawer dropdown) and the setter agree.
export const laneStatusLocked = (lane: Lane): boolean =>
    lane.totes > 0 || lane.auctions > 0;

// Manual status changes have no backend home yet (lane status is still
// FE-mocked), so they're persisted per-device in localStorage and layered
// over the mock on every fetch. When the real lane feed lands, replace
// this store with a BE write.
// Bumped to "-v2" to discard any earlier per-lane manual picks as part of
// the board reset to all-empty — old overrides under the v1 key are
// abandoned. New manual changes write here and persist as normal.
const MANUAL_STATUS_KEY = "lanes-manual-status-v2";

const readManualStatuses = (): Record<string, StatusSlug> => {
    try {
        const raw = window.localStorage.getItem(MANUAL_STATUS_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? (parsed as Record<string, StatusSlug>) : {};
    } catch {
        return {};
    }
};

const writeManualStatus = (barcode: string, status: StatusSlug): void => {
    try {
        const all = readManualStatuses();
        all[barcode] = status;
        window.localStorage.setItem(MANUAL_STATUS_KEY, JSON.stringify(all));
    } catch { /* storage disabled */ }
};

export const useLanes = (warehouseId?: string): UseLanesResult => {
    const [allLanes, setAllLanes] = useState<Lane[]>([]);
    const [warehouses, setWarehouses] = useState<string[]>([]);
    const [refreshing, setRefreshing] = useState(false);
    const [initialLoaded, setInitialLoaded] = useState(false);
    const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
    const [error, setError] = useState(false);
    // Guard against races when refresh is clicked twice in quick
    // succession — only the latest response is allowed to commit.
    const fetchSeq = useRef(0);

    const refresh = useCallback(async () => {
        const seq = ++fetchSeq.current;
        setRefreshing(true);
        try {
            const res = await apiFetch(`/put-away/locations`, {});
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const rows: LocationRow[] = await res.json();
            if (seq !== fetchSeq.current) return;

            const allWarehouses = new Set<string>();
            for (const r of rows) if (r.warehouse) allWarehouses.add(r.warehouse);

            // Layer any per-device manual status overrides over the mock.
            // Totes/auctions stay as the mock derived them — an override is
            // only ever set on an empty lane, so this can't resurrect a
            // locked lane into an inconsistent state.
            const manual = readManualStatuses();
            const lanes: Lane[] = rows
                .filter(r => r.type === "lane")
                .map(r => {
                    const lane: Lane = {
                        barcode: r.barcode,
                        warehouse: r.warehouse,
                        zone: r.zone,
                        walk_order: r.walk_order,
                        ...mockLane(r.barcode),
                    };
                    const override = manual[r.barcode];
                    if (override) lane.status = override;
                    return lane;
                });

            setWarehouses(Array.from(allWarehouses).sort((a, b) => a.localeCompare(b)));
            setAllLanes(lanes);
            setLastUpdatedAt(Date.now());
            setError(false);
        } catch {
            // Don't blank the screen. PWA pages keep the last-good data
            // and surface staleness via the toolbar's `Stale` tag.
            if (seq === fetchSeq.current) setError(true);
        } finally {
            if (seq === fetchSeq.current) {
                setRefreshing(false);
                setInitialLoaded(true);
            }
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    // Manual status change. The empty-lane lock is enforced here too (not
    // just in the dropdown) so the rule can't be bypassed.
    const setLaneStatus = useCallback((barcode: string, status: StatusSlug) => {
        setAllLanes(prev => {
            const target = prev.find(l => l.barcode === barcode);
            if (!target || laneStatusLocked(target)) return prev;
            writeManualStatus(barcode, status);
            return prev.map(l => (l.barcode === barcode ? { ...l, status } : l));
        });
    }, []);

    const lanes = warehouseId
        ? allLanes.filter(l => l.warehouse === warehouseId)
        : allLanes;

    return {
        lanes,
        allLanes,
        warehouses,
        refresh,
        refreshing,
        initialLoaded,
        lastUpdatedAt,
        error,
        setLaneStatus,
    };
};
